import { motion } from 'framer-motion';
import { AlertTriangle, Wheat, Egg, Milk, Nut } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

export default function AllergenPage() {
  const allergens = [
    {
      icon: Wheat,
      name: 'Gluten',
      description: 'Present in wheat flour, which is used in most of our baked goods including cakes, cupcakes, and bread.',
      products: 'All cakes, cupcakes, pastries, and bread products',
      color: 'bg-amber-100 text-amber-800 border-amber-200'
    },
    {
      icon: Egg,
      name: 'Eggs',
      description: 'Used in our cake batters, frostings, and many other recipes for binding and structure.',
      products: 'All cakes, cupcakes, and most pastries',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    },
    {
      icon: Milk,
      name: 'Dairy',
      description: 'Including milk, butter, and cream used in our recipes and frostings.',
      products: 'All cakes, cupcakes, buttercream, and cream-based frostings',
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      icon: Nut,
      name: 'Tree Nuts',
      description: 'Some of our products contain nuts or may have been prepared in an environment where nuts are present.',
      products: 'Fruit cakes, certain specialty items (clearly labeled)',
      color: 'bg-orange-100 text-orange-800 border-orange-200'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="allergen-page">
      {/* Header */}
      <section className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <span className="font-handwritten text-2xl text-[#8D6E63]">Important Information</span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1a1a1a]">
              Allergen Guide
            </h1>
            <p className="text-stone-600 mt-4 max-w-2xl">
              Your safety is our priority. Please review our allergen information carefully before placing an order.
              If you have any concerns, don't hesitate to contact us.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Warning Banner */}
      <section className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-amber-800 text-sm">
              <strong>Cross-contamination notice:</strong> Our bakery handles gluten, eggs, dairy, and nuts. 
              We cannot guarantee any product is completely free from allergens.
            </p>
          </div>
        </div>
      </section>

      {/* Allergen Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {allergens.map((allergen, idx) => (
              <motion.div
                key={allergen.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-stone-100"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${allergen.color}`}>
                    <allergen.icon className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#1a1a1a]">
                      {allergen.name}
                    </h3>
                    <p className="text-stone-600 mt-2 text-sm">
                      {allergen.description}
                    </p>
                    <div className="mt-4 p-3 bg-stone-50 rounded-lg">
                      <p className="text-sm">
                        <span className="font-medium text-[#1a1a1a]">Found in: </span>
                        <span className="text-stone-600">{allergen.products}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-6">
            Special Dietary Requirements
          </h2>
          
          <div className="space-y-6 text-stone-600">
            <p>
              We understand that dietary requirements can be complex. While we are currently unable to 
              offer completely allergen-free products due to our kitchen setup, we are always happy to 
              discuss your specific needs.
            </p>
            
            <div className="bg-[#8D6E63]/10 rounded-xl p-6">
              <h3 className="font-semibold text-[#1a1a1a] mb-2">Have questions?</h3>
              <p className="text-sm mb-4">
                If you have specific allergen concerns or questions about any of our products, 
                please contact us before placing your order.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="mailto:madbrotherscakes@gmail.com">
                  <Button className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full">
                    Email Us
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full border-[#1a1a1a]">
                    Contact Page
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
